import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../lib/api.js';

import RelativeTime from '../components/shared/RelativeTime.jsx';
function CreateAdminModal({ onClose, onSubmit, isPending, error }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Create VISTA Admin</h3>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit({ name, email }); }}
          className="mt-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Jane Smith" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="jane@colorado-communications.com" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeactivateDialog({ userName, onConfirm, onCancel, isPending }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Deactivate Admin</h3>
        <p className="mt-2 text-sm text-gray-600">
          Deactivate <span className="font-semibold">{userName}</span>? They will lose all platform access immediately.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} disabled={isPending}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
            {isPending ? 'Deactivating...' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  vista_admin: 'bg-green-100 text-green-700',
  deactivated: 'bg-red-100 text-red-600',
};

export default function PlatformUsers() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => api.get('/vista-admin/users'),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/vista-admin/users', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setShowCreate(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId) => api.delete(`/vista-admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setDeactivatingUser(null);
    },
  });

  const allUsers = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  const vistaAdmins = allUsers.filter((u) => u.role === 'vista_admin' || u.role === 'deactivated');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Users</h1>
          {!isLoading && <p className="mt-1 text-sm text-gray-500">{vistaAdmins.length} administrator{vistaAdmins.length !== 1 ? 's' : ''}</p>}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          Create VISTA Admin
        </button>
      </div>

      {showCreate && (
        <CreateAdminModal
          onClose={() => { setShowCreate(false); createMutation.reset(); }}
          onSubmit={(body) => createMutation.mutate(body)}
          isPending={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error?.body?.error || createMutation.error?.message) : null}
        />
      )}

      {deactivatingUser && (
        <ConfirmDeactivateDialog
          userName={deactivatingUser.name}
          onConfirm={() => deactivateMutation.mutate(deactivatingUser.user_id)}
          onCancel={() => setDeactivatingUser(null)}
          isPending={deactivateMutation.isPending}
        />
      )}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Loading...</div>
      ) : vistaAdmins.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <div className="text-4xl">⊕</div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">No platform administrators</h2>
          <p className="mt-2 text-sm text-gray-500">Create the first VISTA Admin to get started.</p>
          <button onClick={() => setShowCreate(true)}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            Create VISTA Admin
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Last Login</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vistaAdmins.map((u) => {
                const isSelf = u.user_id === currentUser?.user_id;
                return (
                  <tr key={u.user_id}>
                    <td className="px-4 py-3 font-medium">
                      {u.name}
                      {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                    <td className="px-4 py-3 text-gray-500"><RelativeTime ts={u.last_login} fallback="Never" /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[u.role] || 'bg-gray-100 text-gray-500'}`}>
                        {u.role === 'deactivated' ? 'Deactivated' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.role !== 'deactivated' && (
                        isSelf ? (
                          <span className="text-xs text-gray-300" title="Cannot deactivate your own account">Deactivate</span>
                        ) : (
                          <button onClick={() => setDeactivatingUser(u)}
                            className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50">
                            Deactivate
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
